import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AuraLayout,
  Button,
  CashIcon,
  CircularProgress,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { BodyCopy } from "../../components/BodyCopy";
import {
  StepsCarousel,
  type CarouselStep,
} from "../../components/lola/StepsCarousel";
import { useStore } from "../../store/globalState";
import { useGalleryStore } from "../../gallery/galleryStore";
import { accentInkOnSurface } from "../../utils/accentInkOnSurface";
import { APP_ROUTES, LOLA_ROUTES, TITLE_COLOR } from "../../constants";
import step1 from "../../assets/cash_deposit_how_to/step1.svg?raw";
import step2 from "../../assets/cash_deposit_how_to/step2.svg?raw";
import step3 from "../../assets/cash_deposit_how_to/step3.svg?raw";
import step4 from "../../assets/cash_deposit_how_to/step4.svg?raw";
import step5 from "../../assets/cash_deposit_how_to/step5.svg?raw";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


const ILLUSTRATIONS = [step1, step2, step3, step4, step5];

/** Steps that quote the exact phrase to say at the counter. */
const SCRIPTED_STEPS = new Set(["3"]);

const STEP_KEYS = ["1", "2", "3", "4", "5"] as const;

/** Mirrors `totalPayment` on the Lola confirmation mock. */
const CASH_TO_BRING = "$403.11";

/**
 * Lola "how to pay in cash" — v2 of the numbered-timeline screen.
 *
 * Same four beats, told one at a time in a carousel that advances on its own
 * and hands control to the user on the first swipe. Footer pairs the store
 * locator (secondary) with the WhatsApp return (primary).
 */
export const HowToCashDepositPage = memo(() => {
  const { t } = useTranslation("lola");
  const navigate = useNavigate();
  const { theme } = useStore();
  const partnerColors = useGalleryStore(
    (s) => s.rawTheme.colors as Record<string, string>,
  );
  const inactiveColor = partnerColors.inactived;

  const colorConfig = theme.whiteView;
  const {
    background,
    backgroundBtn,
    textColorBtn,
    title,
    bodyCopy,
    footerColor,
    dropzoneColors,
  } = colorConfig;

  const steps = useMemo<CarouselStep[]>(
    () =>
      STEP_KEYS.map((key, position) => ({
        id: key,
        illustration: ILLUSTRATIONS[position],
        title: t(`howToCashDeposit.steps.${key}.title`),
        body: t(`howToCashDeposit.steps.${key}.body`, {
          amount: CASH_TO_BRING,
        }),
        script: SCRIPTED_STEPS.has(key)
          ? t(`howToCashDeposit.steps.${key}.script`)
          : undefined,
      })),
    [t],
  );

  /**
   * The artwork ships a purple palette; these tokens repaint it from the
   * active theme. `color-mix` keeps the tint ladder proportional whatever the
   * partner's brand color is, so the drawings never fight the chrome.
   */
  const accentSolid = accentInkOnSurface(partnerColors, background, title);

  const themeVars = {
    "--cdh-ink": accentSolid,
    "--cdh-mid": `color-mix(in srgb, ${accentSolid} 55%, ${background})`,
    "--cdh-soft": `color-mix(in srgb, ${accentSolid} 22%, ${background})`,
    "--cdh-fill": `color-mix(in srgb, ${accentSolid} 10%, ${background})`,
    "--cdh-neutral": inactiveColor ?? footerColor,
    "--cdh-neutral-strong": footerColor,
    "--cdh-white": background,
    "--cdh-title": accentSolid,
    "--cdh-body": bodyCopy,
    "--cdh-muted": footerColor,
    "--cdh-accent": accentSolid,
    "--cdh-surface": background,
    "--cdh-border": inactiveColor ?? bodyCopy,
  } as React.CSSProperties;

  return (
    <AuraLayout colorConfig={colorConfig} className="cdh-page">
      <Layout.Header style={themeVars}>
        <Navbar
          title=""
          color={title}
          onBackClick={() => navigate(LOLA_ROUTES.CONFIRMATION)}
        />
      </Layout.Header>

      <Layout.Content className="cdh-section" style={themeVars}>
        <ScrollFadeIndicator color={bodyCopy} />
        <section className="cdh-hero">
          <CircularProgress
            variant="full"
            size={56}
            strokeWidth={3}
            colors={dropzoneColors}
            className="cdh-hero-mark"
          >
            <span className="cdh-hero-mark-inner" style={{ background }}>
              <CashIcon size={22} colors={dropzoneColors} />
            </span>
          </CircularProgress>
          <PageTitle
            highlight={t("howToCashDeposit.titleHighlight")}
            highlightColor={TITLE_COLOR}
            secudnary={t("howToCashDeposit.titleSecondary")}
            secudnaryColor={TITLE_COLOR}
          />
        </section>

        <StepsCarousel
          steps={steps}
          regionLabel={t("howToCashDeposit.title")}
          stepLabel={(position, total) =>
            t("howToCashDeposit.stepOf", { current: position + 1, total })
          }
          goToLabel={(position) =>
            t("howToCashDeposit.goToStep", { step: position + 1 })
          }
        />
      </Layout.Content>

      <Layout.Footer style={themeVars}>
        <section className="footer-section cdh-footer">
          <BodyCopy textColor={footerColor} className="cdh-store-count">
            {t("howToCashDeposit.storeCount")}
          </BodyCopy>
          <Button
            type="button"
            size="large"
            variant="outline"
            className="cdh-secondary-cta"
            /* `outline` paints its ring from `background`, so the secondary CTA
               echoes the primary's gradient instead of the kit's black default. */
            background={backgroundBtn}
            color={accentSolid}
            style={{ backgroundColor: background }}
            onClick={() => navigate(`${APP_ROUTES.STORES_MAP_PAGE}?from=lola`)}
          >
            {t("howToCashDeposit.findStores")}
          </Button>
          <Button
            type="button"
            size="large"
            className="lola-return-chat"
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => navigate(LOLA_ROUTES.SENDING)}
          >
            {t("howToCashDeposit.backToChat")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
